import type { PersonProfile } from '@shared/views';
import type { ServicePeriod } from '@shared/types';
/* ⚠ ตัวช่วยจัดรูปแบบทั้งชุดย้ายไป pdfKit.ts แล้ว ห้ามเขียนกลับมาไว้ที่นี่
   เอกสารสองใบที่ออกจากระบบเดียวกันต้องใช้วันเดือนปี หัวข้อ และเส้นตารางชุดเดียวกัน */
import {
  bareLevel as bareLevelOf,
  boxLayout,
  DOC_STYLES,
  docFooter,
  factBox as cell,
  levelLabel,
  loadPdfMake,
  monthsTH,
  PAGE_MARGINS,
  pdfFileName,
  rowLayout,
  sinceText,
  td,
  thaiDate,
  untilText,
  type Row,
} from './pdfKit.js';

/**
 * สร้างไฟล์ PDF ประวัติรายบุคคล
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมไม่ใช้ "พิมพ์แล้วเลือก Save as PDF" ของเบราว์เซอร์อย่างเดียว
 * ────────────────────────────────────────────────────────────────
 *
 * ปุ่มพิมพ์ยังอยู่และยังใช้ได้ แต่ผลลัพธ์ขึ้นกับเบราว์เซอร์ของแต่ละคน
 * ขอบกระดาษ หัว-ท้ายกระดาษที่เบราว์เซอร์แถมมา และชื่อไฟล์ ไม่เหมือนกันสักเครื่อง
 * เอกสารที่ต้องแนบเรื่องเสนอผู้บริหารควรออกมาหน้าตาเดียวกันทุกครั้ง ไม่ว่าใครกด
 *
 * ────────────────────────────────────────────────────────────────
 *  ทำไมไม่ถ่ายภาพหน้าจอแล้วยัดลง PDF
 * ────────────────────────────────────────────────────────────────
 *
 * วิธีนั้น (html2canvas) ได้ไฟล์ที่เป็นรูปภาพ ค้นหาข้อความไม่ได้ คัดลอกไม่ได้
 * ตัวหนังสือแตกเวลาซูม และไฟล์ใหญ่กว่ามาก
 *
 * ที่นี่ประกอบเอกสารขึ้นใหม่เป็นข้อความจริง ผลคือค้นคำในไฟล์ได้ คัดลอกชื่อกับ
 * เลขที่คำสั่งไปวางต่อได้ และไฟล์เล็กกว่าภาพหลายเท่า
 *
 * ────────────────────────────────────────────────────────────────
 *  สิ่งที่ต้องอยู่ในไฟล์เสมอ
 * ────────────────────────────────────────────────────────────────
 *
 * ⚠ เดิมมีย่อหน้ากำกับว่าข้อมูลประกอบเป็นของสมมุติ — ผู้ใช้สั่งเอาออกทั้งระบบ
 *   ฟิลด์ origin ของแต่ละส่วนยังส่งมาจากเซิร์ฟเวอร์เหมือนเดิม ถ้าจะเอากลับมาใส่ที่นี่
 * เพราะไฟล์ PDF คือสิ่งที่จะถูกส่งต่อทางอีเมลและปรินต์แจกในห้องประชุม
 * ซึ่งเป็นจุดที่หลุดจากบริบทของหน้าจอไปแล้ว
 */

/** ดาวน์โหลดไฟล์ PDF ของคนคนหนึ่ง */
export async function downloadProfilePdf(p: PersonProfile): Promise<void> {
  const pdfMake = await loadPdfMake();

  pdfMake.createPdf(buildDocument(p)).download(fileName(p));
}

/**
 * ชื่อไฟล์
 *
 * ตัดอักขระที่ระบบไฟล์ของวินโดวส์ไม่ยอมรับออก และตัดคำนำหน้าชื่อทิ้ง
 * เพราะ "นาย/นาง/นางสาว" ทำให้ไฟล์ในโฟลเดอร์เรียงกองอยู่ที่ตัว น. หมด
 */
function fileName(p: PersonProfile): string {
  const bare = p.fullName.replace(/^(นาย|นางสาว|นาง|ว่าที่ ?ร้อยตรี)\s*/, '').trim();
  return pdfFileName(['ประวัติ', bare || p.personaId]);
}

function buildDocument(p: PersonProfile) {
  const unit = (p.current?.unitTrail ?? []).map((u) => u.name);
  return {
    info: {
      title: `ประวัติ ${p.fullName}`,
      subject: 'ประวัติการดำรงตำแหน่งและข้อมูลประกอบ',
      creator: 'YANA ฐานข้อมูลบุคลากร',
    },
    pageSize: 'A4',
    pageMargins: PAGE_MARGINS,
    defaultStyle: { font: 'Sarabun', fontSize: 10.5, lineHeight: 1.35, color: '#101a2c' },

    footer: docFooter(),

    content: [
      { text: p.fullName, style: 'h1' },
      {
        text: [
          p.current ? p.current.positionTitle : 'ไม่พบตำแหน่งที่ครองอยู่ในทะเบียนอัตรากำลัง',
          p.current ? `  ·  ${levelLabel(p.current.level)}` : '',
        ].join(''),
        style: 'lead',
      },
      { text: unit.join('  ›  ') || '—', style: 'unit' },

      { text: 'ข้อมูลทั่วไป', style: 'h2' },
      {
        table: {
          widths: ['*', '*', '*', '*'],
          body: [
            [
              cell('อายุ', p.age != null ? `${p.age} ปี` : '—', `เกิด ${thaiDate(p.birthDate)}`),
              cell(
                'เกษียณอายุราชการ',
                thaiDate(p.retireDate),
                p.retireFiscalYear ? `ปีงบประมาณ ${p.retireFiscalYear} · ${untilText(p.retireDate)}` : '',
              ),
              cell(
                'อายุราชการ',
                sinceText(p.current?.entryDate ?? null),
                `บรรจุ ${thaiDate(p.current?.entryDate ?? null)}`,
              ),
              cell(
                'ครองตำแหน่งปัจจุบัน',
                sinceText(p.current?.occupyDate ?? null),
                `ตั้งแต่ ${thaiDate(p.current?.occupyDate ?? null)}`,
              ),
            ],
          ],
        },
        layout: boxLayout,
        margin: [0, 0, 0, 4] as [number, number, number, number],
      },
      p.current?.posNo
        ? { text: `เลขที่ตำแหน่ง ${p.current.posNo}`, style: 'small', margin: [0, 4, 0, 0] as [number, number, number, number] }
        : {},

      { text: 'ประวัติการดำรงตำแหน่ง', style: 'h2' },
      {
        // ⚠ เดิมบรรทัดนี้ต่อท้ายว่า "ข้อมูลจริงจากทะเบียนประวัติ DPIS"
        // เอาออกตามที่ผู้ใช้ขอ และเป็นการเอาออกที่ถูกต้องด้วย — เอกสารที่พิมพ์ออกไป
        // ไม่ควรรับรองความจริงของข้อมูลแทนต้นทาง ในเมื่อไฟล์ที่ระบบอ่านอาจเป็นชุดสาธิต
        // แถบเตือนของจริงคือบล็อก synthetic ด้านล่าง ซึ่งตัดสินจากค่าที่เซิร์ฟเวอร์ส่งมา
        text: `${p.careerHistory.items.length} รายการ · ${careerSpan(p.careerHistory.items)}`,
        style: 'small',
        margin: [0, 0, 0, 6] as [number, number, number, number],
      },
      historyTable(p.careerHistory.items),

      section('การศึกษา', p.education.items.map((e) => [
        [e.degree, e.field].filter(Boolean).join(' ') || '—',
        [e.institution, e.graduationYear && `จบปี ${e.graduationYear}`].filter(Boolean).join(' · '),
      ])),

      section('การฝึกอบรม', p.training.items.map((t) => [
        t.course ?? '—',
        [t.provider, t.startDate && thaiDate(t.startDate), t.hours && `${t.hours} ชั่วโมง`]
          .filter(Boolean)
          .join(' · '),
      ])),

      section('ผลการประเมินการปฏิบัติราชการ', [...p.performance.items].reverse().map((r) => [
        `ปี ${r.year ?? '—'}`,
        [r.rating, r.score != null && String(r.score)].filter(Boolean).join(' · '),
      ])),

      section('สมรรถนะ (เต็ม 5 คะแนน)', Object.entries(p.competencies.items).map(([k, v]) => [
        COMPETENCY_TH[k] ?? k,
        v.toFixed(1),
      ])),

      section('ภาษา', p.languages.items.map((l) => [
        l.language ?? '—',
        `ฟัง ${l.listening ?? '—'} · พูด ${l.speaking ?? '—'} · อ่าน ${l.reading ?? '—'} · เขียน ${l.writing ?? '—'}`,
      ])),

      section('ผลงาน', p.achievements.items.map((a) => [
        a.title ?? '—',
        [a.year, a.role, a.level, a.impact].filter(Boolean).join(' · '),
      ])),

      section('รางวัล', p.awards.items.map((a) => [
        a.title ?? '—',
        [a.year, a.level].filter(Boolean).join(' · '),
      ])),

      /* เครื่องราชอิสริยาภรณ์ — เอกสารราชการอ้างด้วยอักษรย่อ จึงต้องมีทั้งย่อและเต็ม */
      section('เครื่องราชอิสริยาภรณ์', p.decorations.items.map((d) => [
        [d.shortName, d.name].filter(Boolean).join(' '),
        d.year ? `พ.ศ. ${d.year}` : '—',
      ]), 'ไม่มีรายการในไฟล์ข้อมูล — อาจยังไม่เคยได้รับ หรือไม่อยู่ในข่ายขอพระราชทาน'),

      section(
        'ประวัติทางวินัย',
        p.disciplinary.items.records.map((d) => [
          d.type ?? '—',
          [d.year, d.status].filter(Boolean).join(' · '),
        ]),
        p.disciplinary.items.hasAction ? undefined : 'ไม่มีเรื่องที่บันทึกไว้',
      ),
    ],

    styles: DOC_STYLES,
  };
}

/**
 * ตารางประวัติการดำรงตำแหน่ง เรียงจากใหม่ไปเก่า
 *
 * ใช้ตาราง ไม่ใช่ไทม์ไลน์แบบบนหน้าจอ เพราะบนกระดาษที่ข้ามหน้าได้
 * หัวตารางซ้ำทุกหน้าอ่านง่ายกว่าเส้นแกนที่ขาดตอนกลางหน้า
 */
function historyTable(periods: ServicePeriod[]) {
  if (periods.length === 0) {
    return { text: 'ไม่มีประวัติการดำรงตำแหน่งในข้อมูลชุดนี้', style: 'none' };
  }

  const body: Row[] = [
    [td('ตำแหน่ง', 'th'), td('ระดับ', 'th'), td('ช่วงเวลา', 'th'), td('ระยะเวลา', 'th')],
  ];

  for (const h of [...periods].reverse()) {
    const tags = [h.acting ? 'รักษาการ' : '', h.isCurrent ? 'ปัจจุบัน' : ''].filter(Boolean);
    // เลขที่คำสั่งอยู่ใต้ชื่อตำแหน่ง ไม่ใช่คอลัมน์ของตัวเอง
    // ห้าคอลัมน์บนกระดาษ A4 แนวตั้งทำให้ทั้งชื่อตำแหน่งและเลขที่คำสั่งถูกบีบจนขึ้นบรรทัดใหม่ทุกแถว
    const title = [
      h.title,
      tags.length ? ` (${tags.join(' · ')})` : '',
      h.orderNo ? `\nคำสั่งที่ ${h.orderNo}` : '',
    ].join('');

    body.push([
      td(title),
      /* ⚠ ระดับมีสามทาง — ตาม พ.ร.บ. 2551 · ซีเดิม · ระดับของบุคลากรประเภทอื่น
         ต้องเลือกทางที่มีค่า ไม่ใช่เขียน level ตรง ๆ แล้วปล่อยว่างเมื่อเป็นประวัติเก่า */
      td(bareLevelOf(h.level ?? h.legacyLevel ?? h.nonCivilServiceLevel)),
      td(`${thaiDate(h.start)} – ${h.end ? thaiDate(h.end) : 'ปัจจุบัน'}`),
      // ห้ามให้ระยะเวลาขึ้นบรรทัดใหม่ — '1 ปี 10 เดือน' ที่แตกเป็นสองบรรทัด
      // ทำให้ทั้งแถวสูงขึ้นเพื่อคำเดียว และตารางดูขาดเป็นช่วง ๆ
      td(monthsTH(h.months), undefined, true),
    ]);
  }

  return {
    /*
     * ความกว้างสามคอลัมน์ท้ายวัดจากค่าที่ยาวที่สุดที่มีอยู่จริงในข้อมูล
     *   ระดับ    'ชำนาญการพิเศษ'
     *   ช่วงเวลา '27 ธ.ค. 2545 – 17 พ.ย. 2547'  (เดือนย่อสี่ตัวอักษรทั้งคู่ = กรณีที่ยาวที่สุด)
     *   ระยะเวลา '1 ปี 10 เดือน'
     * แคบกว่านี้แล้วขึ้นบรรทัดใหม่ ตารางสูงขึ้นและอ่านยากกว่าเดิม
     */
    table: { headerRows: 1, widths: ['*', 86, 142, 72], body },
    layout: rowLayout,
  };
}

/**
 * ช่วงเวลารวมของเส้นทางราชการ — "ตั้งแต่ … รวม … ปี … เดือน"
 *
 * ⚠ อยู่ที่ไฟล์นี้ ไม่ได้ย้ายไป pdfKit เพราะเป็นบรรทัดสรุปเฉพาะของเอกสารประวัติ
 *   เอกสารอัตราไม่มีบรรทัดนี้ (มันสรุปเรื่องอัตรา ไม่ใช่เรื่องคน)
 */
function careerSpan(periods: ServicePeriod[]): string {
  if (periods.length === 0) return 'ไม่มีข้อมูล';
  const total = periods.reduce((sum, h) => sum + h.months, 0);
  return `ตั้งแต่ ${thaiDate(periods[0]?.start ?? null)} รวม ${monthsTH(total)}`;
}

/** หนึ่งหัวข้อในส่วนข้อมูลประกอบ — ว่างก็ยังต้องมีหัวข้อ ไม่ใช่หายไปเฉย ๆ */
function section(title: string, rows: string[][], emptyText = 'ไม่มีข้อมูล') {
  if (rows.length === 0) {
    return {
      stack: [
        { text: title, style: 'h2' },
        { text: emptyText, style: 'none' },
      ],
      unbreakable: true,
    };
  }

  return {
    stack: [
      { text: title, style: 'h2' },
      {
        table: { widths: [170, '*'], body: rows.map((r) => r.map((c) => td(c))) },
        layout: rowLayout,
      },
    ],
  };
}

const COMPETENCY_TH: Record<string, string> = {
  leadership: 'ภาวะผู้นำ',
  strategicThinking: 'การคิดเชิงยุทธศาสตร์',
  changeManagement: 'การบริหารการเปลี่ยนแปลง',
  communication: 'การสื่อสาร',
  digitalLiteracy: 'ทักษะดิจิทัล',
  teamwork: 'การทำงานเป็นทีม',
};
